<script>
import { mapGetters } from 'vuex';
import ConversationHeader from './ConversationHeader.vue';
import DashboardAppFrame from '../DashboardApp/Frame.vue';
import EmptyState from './EmptyState/EmptyState.vue';
import MessagesView from './MessagesView.vue';
import QALiveMonitorFilterBar from '../../QALiveMonitorFilterBar.vue';

export default {
  components: {
    ConversationHeader,
    DashboardAppFrame,
    EmptyState,
    MessagesView,
    QALiveMonitorFilterBar,
  },
  props: {
    inboxId: {
      type: [Number, String],
      default: '',
      required: false,
    },
    isInboxView: {
      type: Boolean,
      default: false,
    },
    isContactPanelOpen: {
      type: Boolean,
      default: true,
    },
    isOnExpandedLayout: {
      type: Boolean,
      default: true,
    },
    isQaMonitor: {
      type: Boolean,
      default: false,
    },
    hideReplyBox: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['qaFiltersApply', 'qaFiltersClear'],
  data() {
    return { activeIndex: 0 };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      dashboardApps: 'dashboardApps/getRecords',
    }),
    isQaMonitorRoute() {
      const routeName = String(this.$route?.name || '');
      const routePath = String(this.$route?.path || '');
      return (
        routeName.startsWith('qa_monitor') || routePath.includes('/qa-monitor')
      );
    },
    isQaMonitorMode() {
      return this.isQaMonitor || this.isQaMonitorRoute;
    },
    dashboardAppTabs() {
      return [
        {
          key: 'messages',
          index: 0,
          name: this.$t('CONVERSATION.DASHBOARD_APP_TAB_MESSAGES'),
        },
        ...this.dashboardApps.map((dashboardApp, index) => ({
          key: `dashboard-${dashboardApp.id}`,
          index: index + 1,
          name: dashboardApp.title,
        })),
      ];
    },
    showContactPanel() {
      return this.isContactPanelOpen && this.currentChat.id;
    },
  },
  watch: {
    'currentChat.inbox_id': {
      immediate: true,
      handler(inboxId) {
        if (inboxId) {
          this.$store.dispatch('inboxAssignableAgents/fetch', [inboxId]);
        }
      },
    },
    'currentChat.id'() {
      this.fetchLabels();
      this.activeIndex = 0;
    },
  },
  mounted() {
    this.fetchLabels();
    this.$store.dispatch('dashboardApps/get');
  },
  methods: {
    fetchLabels() {
      if (!this.currentChat.id) {
        return;
      }
      this.$store.dispatch('conversationLabels/get', this.currentChat.id);
    },
    onDashboardAppTabChange(index) {
      this.activeIndex = index;
    },
  },
};
</script>

<template>
  <div
    class="conversation-details-wrap flex flex-col min-w-0 w-full relative overflow-hidden"
    style="background-color: var(--wlab-bg-primary)"
    :class="{
      'border-l rtl:border-l-0 rtl:border-r border-n-weak':
        !isOnExpandedLayout && !isQaMonitorMode,
      'rounded-tr-2xl rounded-br-2xl': !isQaMonitorMode,
    }"
  >
    <ConversationHeader
      v-if="currentChat.id && !isQaMonitorMode"
      :chat="currentChat"
      :show-back-button="isOnExpandedLayout && !isInboxView"
    />
    <div
      v-if="isQaMonitorMode && !dashboardApps.length"
      class="w-full min-h-[56px] border-b border-n-weak px-4 flex items-center"
      style="background-color: var(--wlab-header-bg)"
    >
      <QALiveMonitorFilterBar
        @apply-filters="$emit('qaFiltersApply', $event)"
        @clear-filters="$emit('qaFiltersClear')"
      />
    </div>
    <woot-tabs
      v-if="dashboardApps.length && currentChat.id"
      :index="activeIndex"
      class="-mt-px border-t border-t-n-background"
      @change="onDashboardAppTabChange"
    >
      <woot-tabs-item
        v-for="tab in dashboardAppTabs"
        :key="tab.key"
        :index="tab.index"
        :name="tab.name"
        :show-badge="false"
        is-compact
        class="[&_a]:pt-1"
      />
    </woot-tabs>
    <div v-show="!activeIndex" class="flex h-full min-h-0 m-0">
      <MessagesView
        v-if="currentChat.id"
        :inbox-id="inboxId"
        :is-qa-monitor="isQaMonitor"
        :is-inbox-view="isInboxView"
        :hide-reply-box="hideReplyBox"
      />
      <EmptyState
        v-if="!currentChat.id && !isInboxView"
        :is-on-expanded-layout="isOnExpandedLayout"
      />
      <slot />
    </div>
    <DashboardAppFrame
      v-for="(dashboardApp, index) in dashboardApps"
      v-show="activeIndex - 1 === index"
      :key="currentChat.id + '-' + dashboardApp.id"
      :is-visible="activeIndex - 1 === index"
      :config="dashboardApps[index].content"
      :position="index"
      :current-chat="currentChat"
    />
  </div>
</template>

<style scoped>
@media (max-width: 768px) {
  .conversation-details-wrap {
    border-top-right-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
    border-right: 0 !important;
  }
}
</style>
